import {
  JourneyModel,
  JourneysStore,
  selectAllJourneys,
  selectSelectedJourney,
} from '@des/store/journeys';
import {
  select,
} from '@ngrx/store';
import {
  combineLatest,
  Observable,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
} from 'rxjs/operators';

/**
 * Gets the departure endpoint for the first Journey in the order
 *
 * @param source The journey store
 * @returns Observable of the departure flight end point for the first journey of the order
 */
const getFirstJourneyDeparture = (source: Observable<JourneysStore>) =>
  combineLatest([
    source.pipe(
      filter((store) => !!store.journeys),
      select(selectSelectedJourney),
      filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey?.journeyElements.length),
      map((selectedJourney) => selectedJourney.journeyElements[0].journeyElement.orderId),
      filter<string>((orderId) => !!orderId)
    ),
    source.pipe(
      filter((store) => !!store.journeys),
      select(selectAllJourneys),
      filter<JourneyModel[]>((journeys) => journeys.length > 0)
    )
  ]).pipe(
    map(([selectedJourneyOrderId, journeys]) => {
      const firstJourneyIndex = journeys.findIndex((journey) => journey.journeyElements[0].journeyElement.orderId === selectedJourneyOrderId);
      const firstJourney = journeys[firstJourneyIndex];
      return firstJourney.flights[0].checkInFlight.departure;
    }),
    distinctUntilChanged((a, b) => a.locationCode === b.locationCode && a.dateTime?.getTime() === b.dateTime?.getTime())
  );

/**
 * Depature location code for the selected journey
 */
export const getDepartureLocationCode = () => (source: Observable<JourneysStore>) =>
  source.pipe(
    filter((store) => !!store.journeys),
    select(selectSelectedJourney),
    filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey),
    map((journey) => journey?.flights[0].checkInFlight.departure.locationCode),
    distinctUntilChanged()
  );

/**
 * Depature location city code for the selected journey
 */
export const getDepartureCityCode = () => (source: Observable<JourneysStore>) =>
  source.pipe(
    filter((store) => !!store.journeys),
    select(selectSelectedJourney),
    filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey),
    map((journey) => journey?.flights[0].checkInFlight.departure.location?.cityCode),
    distinctUntilChanged()
  );

/**
 * Depature location country code for the selected journey
 */
export const getDepartureCountryCode = () => (source: Observable<JourneysStore>) =>
  source.pipe(
    filter((store) => !!store.journeys),
    select(selectSelectedJourney),
    filter<JourneyModel>((journey: JourneyModel | undefined) => !!journey),
    map((journey) => journey?.flights[0].checkInFlight.departure.location?.countryCode),
    distinctUntilChanged()
  );

/**
 * Depature location city code for the first journey
 */
export const getFirstJourneyDepartureCityCode = () => (source: Observable<JourneysStore>) =>
  getFirstJourneyDeparture(source).pipe(
    map((departure) => departure.location?.cityCode),
    distinctUntilChanged()
  );

/**
 * Depature location country code for the first journey
 */
export const getFirstJourneyDepartureCountryCode = () => (source: Observable<JourneysStore>) =>
  getFirstJourneyDeparture(source).pipe(
    map((departure) => departure.location?.countryCode),
    distinctUntilChanged()
  );

/**
 * Depature location country code for the first journey
 */
export const getFirstJourneyDepartureLocationCode = () => (source: Observable<JourneysStore>) =>
  getFirstJourneyDeparture(source).pipe(
    map((departure) => departure.locationCode),
    distinctUntilChanged()
  );
